<script setup>
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "dashboard/composables/store";
import { useAlert } from "dashboard/composables";
/* global axios */

const route = useRoute();
const store = useStore();
const data = ref(null);
const loading = ref(true);

// 拦截日志(定制 2026-08-25): 攻击者拦截记录, 支持逐条/批量处理。
const intercepts = ref([]);
const interceptsLoading = ref(false);
const handlingIds = ref([]);

const fetchData = async () => {
  loading.value = true;
  try {
    const accountId = route.params.accountId;
    const res = await store.dispatch("stormReports/fetch", accountId);
    data.value = res;
  } catch (e) {
    useAlert("报表加载失败", "error");
  } finally {
    loading.value = false;
  }
};

const fetchIntercepts = async () => {
  interceptsLoading.value = true;
  try {
    const { data: res } = await axios.get(
      `/api/v1/accounts/${route.params.accountId}/interception_logs`
    );
    intercepts.value = res.logs || [];
  } catch (e) {
    useAlert("拦截日志加载失败", "error");
  } finally {
    interceptsLoading.value = false;
  }
};

const unhandledIntercepts = () => intercepts.value.filter(i => !i.handled);

const handleIntercept = async log => {
  handlingIds.value = [...handlingIds.value, log.id];
  try {
    await axios.post(
      `/api/v1/accounts/${route.params.accountId}/interception_logs/handle`,
      { id: log.id }
    );
    useAlert("已标记处理", "success");
    await fetchIntercepts();
  } catch (e) {
    useAlert("处理失败", "error");
  } finally {
    handlingIds.value = handlingIds.value.filter(id => id !== log.id);
  }
};

const handleAllIntercepts = async () => {
  const ids = unhandledIntercepts().map(i => i.id);
  if (!ids.length) return;
  try {
    await axios.post(
      `/api/v1/accounts/${route.params.accountId}/interception_logs/handle`,
      { ids }
    );
    useAlert(`已处理 ${ids.length} 条`, "success");
    await fetchIntercepts();
  } catch (e) {
    useAlert("批量处理失败", "error");
  }
};

const fmt = n => (n || 0).toLocaleString();
const fmtTime = ts =>
  ts ? new Date(ts * 1000).toLocaleString("zh-CN", { hour12: false }) : "";

onMounted(() => {
  fetchData();
  fetchIntercepts();
});
</script>

<template>
  <div class="overflow-auto bg-n-surface-1 w-full px-6">
    <div class="max-w-5xl mx-auto py-8">
      <div class="mb-6">
        <h1 class="text-2xl font-semibold text-n-slate-12">风暴报表</h1>
        <p class="text-sm text-n-slate-10 mt-1">每日新增/拦截/清理情况（实时）</p>
      </div>
      <div v-if="loading" class="py-20 text-center text-n-slate-10">加载中...</div>
      <div v-else-if="data" class="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div class="bg-n-solid-3 rounded-xl p-5">
          <div class="text-xs text-n-slate-10 mb-1">当前会话总数</div>
          <div class="text-2xl font-bold text-n-slate-12">{{ fmt(data.totals.conversations) }}</div>
        </div>
        <div class="bg-n-solid-3 rounded-xl p-5">
          <div class="text-xs text-n-slate-10 mb-1">当前联系人总数</div>
          <div class="text-2xl font-bold text-n-slate-12">{{ fmt(data.totals.contacts) }}</div>
        </div>
        <div class="bg-n-solid-3 rounded-xl p-5">
          <div class="text-xs text-n-slate-10 mb-1">当前消息总数</div>
          <div class="text-2xl font-bold text-n-slate-12">{{ fmt(data.totals.messages) }}</div>
        </div>
        <div class="bg-n-blue-4/20 rounded-xl p-5 border border-n-blue-5/30">
          <div class="text-xs text-n-blue-9 mb-1">今日新增联系人</div>
          <div class="text-2xl font-bold text-n-blue-11">{{ fmt(data.today.new_contacts) }}</div>
        </div>
        <div class="bg-n-blue-4/20 rounded-xl p-5 border border-n-blue-5/30">
          <div class="text-xs text-n-blue-9 mb-1">今日新增会话</div>
          <div class="text-2xl font-bold text-n-blue-11">{{ fmt(data.today.new_conversations) }}</div>
        </div>
        <div class="bg-n-amber-4/20 rounded-xl p-5 border border-n-amber-5/30">
          <div class="text-xs text-n-amber-9 mb-1">今日拦截机器人</div>
          <div class="text-2xl font-bold text-n-amber-11">{{ fmt(data.bot_blocked_today) }}</div>
        </div>
      </div>
      <div v-if="data && data.last_cleanup && data.last_cleanup.contacts != null" class="mt-6 bg-n-solid-3 rounded-xl p-5">
        <div class="text-xs text-n-slate-10 mb-2">上次自动清理</div>
        <div class="text-sm text-n-slate-12">
          {{ data.last_cleanup.at }} 清理：联系人 {{ fmt(data.last_cleanup.contacts) }}，会话 {{ fmt(data.last_cleanup.conversations) }}
        </div>
      </div>
      <div class="mt-6 flex items-center gap-3">
        <button class="px-4 py-2 text-sm rounded-lg bg-n-brand text-white hover:opacity-90" @click="fetchData">刷新</button>
      </div>

      <!-- 拦截日志(定制 2026-08-25): 攻击者拦截记录, 处理后状态栏红色提醒消失 -->
      <div class="mt-10">
        <div class="flex items-center justify-between mb-4">
          <div class="flex items-center gap-3">
            <h2 class="text-lg font-semibold text-n-slate-12">拦截日志</h2>
            <span
              v-if="unhandledIntercepts().length"
              class="px-2 py-0.5 rounded-md text-xs font-medium bg-red-500/15 border border-red-500/40 text-red-11"
            >
              未处理 {{ unhandledIntercepts().length }}
            </span>
          </div>
          <button
            v-if="unhandledIntercepts().length"
            class="px-3 py-1.5 text-xs rounded-lg bg-red-500 text-white hover:bg-red-600"
            @click="handleAllIntercepts"
          >
            全部处理
          </button>
        </div>
        <div v-if="interceptsLoading" class="py-8 text-center text-n-slate-10 text-sm">加载中...</div>
        <div v-else-if="!intercepts.length" class="py-8 text-center text-n-slate-10 text-sm bg-n-solid-3 rounded-xl">
          暂无拦截记录
        </div>
        <div v-else class="bg-n-solid-3 rounded-xl overflow-hidden">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs text-n-slate-10 border-b border-n-slate-4">
                <th class="px-4 py-2.5 font-medium">时间</th>
                <th class="px-4 py-2.5 font-medium">联系人</th>
                <th class="px-4 py-2.5 font-medium">邮箱</th>
                <th class="px-4 py-2.5 font-medium">IP</th>
                <th class="px-4 py-2.5 font-medium">拦截规则</th>
                <th class="px-4 py-2.5 font-medium text-right">状态</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="log in intercepts"
                :key="log.id"
                class="border-b border-n-slate-4/50 last:border-0"
                :class="log.handled ? 'text-n-slate-9' : 'text-n-slate-12'"
              >
                <td class="px-4 py-2.5 tabular-nums whitespace-nowrap">{{ fmtTime(log.created_at) }}</td>
                <td class="px-4 py-2.5">{{ log.contact_name || '-' }}</td>
                <td class="px-4 py-2.5 break-all">{{ log.email || '-' }}</td>
                <td class="px-4 py-2.5 tabular-nums">{{ log.ip || '-' }}</td>
                <td class="px-4 py-2.5">
                  <span class="px-2 py-0.5 rounded text-xs bg-n-slate-5 text-n-slate-11">{{ log.rule_label }}</span>
                </td>
                <td class="px-4 py-2.5 text-right whitespace-nowrap">
                  <span v-if="log.handled" class="text-green-11 text-xs">已处理</span>
                  <button
                    v-else
                    class="px-3 py-1 rounded-md text-xs bg-red-500/15 border border-red-500/40 text-red-11 hover:bg-red-500/25"
                    :disabled="handlingIds.includes(log.id)"
                    @click="handleIntercept(log)"
                  >
                    {{ handlingIds.includes(log.id) ? '处理中…' : '处理' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
